// src/components/PushNotification.tsx
import { useEffect } from 'react';
import { onMessage } from 'firebase/messaging';
import { useSnackbar } from '../../context/SnackbarContextProvider';
import { messaging } from '../../firebase/firebaseConfig';
import { SVG } from '../../constants/assetLists';
import { useNotifications } from '../../hooks/useNotifications';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import QUERY_KEYS from '../../constants/queryKeys';

const PushNotification = () => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refetchNotifications } = useNotifications({
    page: 1,
    limit: 10,
  });

  const getAnnouncementStyleByType = (announcementType?: string) => {
    switch (announcementType) {
      case 'Regular':
        return { icon: SVG.regularAnnouncementIcon, bgColor: 'bg-[#ededed]' };

      case 'Error':
        return { icon: SVG.redTriangleIcon, bgColor: 'bg-lightRed' };

      case 'Training':
        return { icon: SVG.tutorialsActiveIcon, bgColor: 'bg-[#ededed]' };

      case 'Administration':
        return { icon: SVG.settingBlackIcon, bgColor: 'bg-[#FFE9A3]' };

      case 'Regulation':
        return { icon: SVG.administrationSmallIcon, bgColor: 'bg-[#ededed]' };

      default:
        return { icon: SVG.regularAnnouncementIcon, bgColor: 'bg-[#ededed]' };
    }
  };

  useEffect(() => {
    if (!messaging) return;
    const unsubscribe = onMessage(messaging, (payload) => {
      // console.log("FCM payload:", payload);
      refetchNotifications();
      const data = payload.data || {};

      if (data.type === 'daily_summary') {
        return showSnackbar({
          icon: SVG.daySummrayIcon,
          message:
            data.title ||
            payload.notification?.title ||
            payload.notification?.body ||
            'סיכום יומי מוכן',
          variant: 'banner',
          type: 'info',
          duration: 8000,
          bgColor: 'bg-[#ededed]',
          actions: (
            <button
              className="text-[#063556] underline font-medium text-2xl"
              onClick={() =>
                window.dispatchEvent(new Event('open-day-summary'))
              }
            >
              פתיחה
            </button>
          ),
        });
      }

      if (data.type === 'checklist_completed' || data.type === 'checklist_due') {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_ALL_CHECKLISTS],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_GROUPED_CHECKLISTS],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_ALL_CHECKLISTS_SHIFT_ID],
        });
        if (data.checklistId) {
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_CHECKLIST, data.checklistId],
          });
        }

        return showSnackbar({
          icon: data.type === 'checklist_completed' ? SVG.completedCheckboxIcon : SVG.clockIcon,
          message:
            data.checklistTitle ||
           payload.notification?.title ||
            payload.notification?.body ||
            '',
          variant: 'banner',
          type: 'success',
          duration: 8000,
          bgColor: 'bg-[#DAFFF4]',
          actions: data.checklistId && (
            <button
              className="text-[#063556] underline font-medium text-2xl"
              onClick={() =>
                navigate('/operations', {
                  state: { checklistApprovalId: data.checklistId },
                })
              }
            >
              פתיחה
            </button>
          ),
        });
      }

      if (data.type === 'onboarding_branch_created') {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_ADMIN_ON_BOARDING_DRAFTS],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_USER_CLIENT],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_ALL_CLIENTS],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_BRANCHES_BY_CLIENT],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_ON_BOARDING_DRAFT],
        });

        const branchLabel = data.subBranchName ? 'תת-הסניף' : 'הסניף';
        const branchName = data.subBranchName || data.branchName;

        return showSnackbar({
          icon: SVG.settingBlackIcon,
          message:
            branchName
              ? `${branchLabel} "${branchName}" נוצר`
              : data.title ||
                payload.notification?.title ||
                'סניף חדש נוצר',
          variant: 'banner',
          type: 'info',
          duration: 8000,
          bgColor: 'bg-[#FFE9A3]',
          actions: (
            <button
              className="text-[#063556] underline font-medium text-2xl"
              onClick={() =>
                navigate(
                  `/on-boarding${data.clientId ? `?clientId=${data.clientId}` : ''}`,
                )
              }
            >
              פתיחה
            </button>
          ),
        });
      }

      const announcementTypes = [
        'announcement',
        'announcement_completed',
        'task',
        'task_completed',
        'form',
      ];
      if (announcementTypes.includes(data.type ?? '')) {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_BY_DATE],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_FOR_STATS],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_BRIEF_ANNOUNCEMENTS_BY_DATE],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_ALL_FORMS],
        });
      }

      const isTaskCompleted = data.type === 'task_completed';
      const body = isTaskCompleted
        ? data.taskTitle || 'Task Completed'
        : data.type === 'task'
          ? data.taskTitle || 'New Task'
          : data.announcementTitle ||
            payload.notification?.body ||
            'New Announcement';
      const announcementId = payload.data?.announcementId;
      const announcementType = payload.data?.announcementType;

      if (data.type === 'form' || data.type === 'regulation_before_date') {
        const formSubmitted = data?.isSubmitted === 'true';
        return showSnackbar({
          message: data?.formTitle || data?.title,
          variant: 'banner',
          type: 'info',
          duration: 8000,
          bgColor: 'bg-[#ededed]',
          actions: data?.type === 'form' && (
            <button
              className="text-[#063556] underline font-medium text-2xl"
              onClick={() =>
                // window.open(data?.url, "_blank", "noopener,noreferrer")
                {
                  if (formSubmitted) {
                    return (window.location.href = `${window.location.origin}/forms`);
                  }
                  window.open(
                    `${window.location.origin}/form/${data?.formId}`,
                    '_blank',
                    'noopener,noreferrer',
                  );
                }
              }
            >
              פתיחה
            </button>
          ),
        });
      }
      if (!announcementId) return;
      const { icon, bgColor } = isTaskCompleted
        ? { icon: SVG.completedCheckboxIcon, bgColor: 'bg-[#DAFFF4]' }
        : getAnnouncementStyleByType(announcementType);
      showSnackbar({
        message: isTaskCompleted ? `משימה הושלמה: ${body}` : body,
        variant: 'banner',
        type: isTaskCompleted ? 'success' : 'info',
        duration: 8000,
        icon,
        bgColor,
        actions: (
          <button
            className="text-[#063556] underline font-medium text-2xl"
            onClick={() =>
              navigate(`/operations`, {
                state: { announcementId: announcementId },
              })
            }
          >
            פתיחה
          </button>
        ),
      });
    });

    return () => unsubscribe();
  }, [showSnackbar]);

  return <></>;
};

export default PushNotification;
